import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

const REGION_ID = "parts-scanner-region";

/** Camera barcode/QR scanner. A USB/Bluetooth scanner can also type into the manual box. */
export const ScannerDialog = ({ open, onOpenChange, onCode }) => {
  const scanner = useRef(null);
  const [error, setError] = useState("");
  const [manual, setManual] = useState("");

  useEffect(() => {
    if (!open) return undefined;
    setError("");
    setManual("");
    let stopped = false;
    const timer = setTimeout(async () => {
      try {
        scanner.current = new Html5Qrcode(REGION_ID);
        await scanner.current.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 260, height: 160 } },
          (text) => {
            if (stopped) return;
            stopped = true;
            onCode(text.trim());
            onOpenChange(false);
          },
          () => {}
        );
      } catch (e) {
        setError("Camera not available. Allow camera access, or type / scan the code into the box below.");
      }
    }, 150);
    return () => {
      stopped = true;
      clearTimeout(timer);
      const s = scanner.current;
      scanner.current = null;
      if (s?.isScanning) s.stop().then(() => s.clear()).catch(() => {});
    };
  }, [open, onCode, onOpenChange]);

  const submitManual = (e) => {
    e.preventDefault();
    if (!manual.trim()) return;
    onCode(manual.trim());
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="scanner-dialog">
        <DialogHeader>
          <DialogTitle>Scan barcode / QR code</DialogTitle>
          <DialogDescription>Point the camera at the part's label.</DialogDescription>
        </DialogHeader>
        <div id={REGION_ID} className="w-full min-h-[220px] rounded-xl overflow-hidden bg-ink" data-testid="scanner-region" />
        {error && <p className="text-sm text-amber-700" data-testid="scanner-error">{error}</p>}
        <form onSubmit={submitManual} className="flex gap-2">
          <Input autoFocus placeholder="Or type / use a handheld scanner" value={manual} onChange={(e) => setManual(e.target.value)} data-testid="scanner-manual-input" />
          <button className="rounded-full bg-ink px-4 text-sm font-bold text-white hover:bg-brand" data-testid="scanner-manual-submit">Go</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
